import AddTurnoverModal from "page-sections/data-table/dataTableV2/AddTurnoverModal";
import { FC, useEffect, useState } from "react"
import { DailyNoteEntity, DailyTicketEntity, DailyTicketingStatusEntity, DepartmentEntity, EmployeeEntity, PaymentMethodEntity, SectionEntity, SimpleDepositEntity, TicketItemEntity } from "api/generated";
import { Button, Container, Grid, useMediaQuery, useTheme, Modal, TextField, IconButton, Box } from "@mui/material";
import { dailyNotesApi, departmentsApi, depositsApi, employeesApi, paymentMethodsApi, paymentSectionsApi, ticketApi, turnoverApi } from "api";
import FlexBox from "components/flexbox/FlexBox";
import { Add, ArrowDownward, ArrowUpward, CheckBox, CheckBoxOutlineBlank, Download, Info, Send } from "@mui/icons-material";
import CalendarInput from "components/input-fields/CalendarInput";
import TurnoverDataTable from "page-sections/data-table/TurnoverDataTable";
import SelectSectionModal from "page-sections/data-table/dataTableV2/SelectSectionModal";
import { useSeason } from "contexts/SeasonContext";
import SearchInput from "components/input-fields/SearchInput";
import {  endOfDay, startOfDay } from "date-fns";
import { getDates , ignoreUTC } from "utils/utils";
import { checkTokenRole } from "utils/checkToken";
import DefaultUserImage from '../../assets/images/defaultUserPage.svg';
import Logo from '../../assets/images/logo_onestaff.png';
import { H2, Small } from "components/Typography";
import { translatePaymentTypes } from "utils/convertPaymentTypes";
import { ApexOptions } from "apexcharts";
import useAuth from "hooks/useAuth";
import OwnerConfirmModal from "page-sections/data-table/dataTableV2/OwnerConfirModal";
import { turnoversReport } from "./turnoverReport";
import AddTicketModal from "page-sections/data-table/dataTableV2/AddTicketModal";
import { useTranslation } from "react-i18next";
import SimplifiedAddDepositModal from "page-sections/data-table/dataTableV2/SimplifiedAddDepositModal";

interface ExtendEmployeeEntity extends EmployeeEntity {
    isRemoved: boolean
}

const TurnoverSummary: FC = () => {
    const { user } = useAuth();
    const [openAddTurnoverModal, setOpenAddTurnoverModal] = useState(false);
    const [sectionModalOpensTicketingModal, setSectionModalOpensTicketingModal] = useState(false);
    const [openAddDepositModal, setOpenAddDepositModal] = useState(false);
    const [openSelectSectionModal, setOpenSelectSectionModal] = useState(false);
    const [depositsDate, setDepositDate] = useState<Date>();
    const { seasonId } = useSeason();
    const [departments, setDepartments] = useState<DepartmentEntity[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<DepartmentEntity>();
    const [sections, setSections] = useState<SectionEntity[]>([]);
    const [selectedSection, setSelectedSection] = useState<SectionEntity>();
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethodEntity[]>([]);
    const [deposits, setDeposits] = useState<SimpleDepositEntity[]>([]);
    const [turnoverEmployees, setTurnoverEmployees] = useState<ExtendEmployeeEntity[]>([]);
    const [dailyNotes, setDailyNotes] = useState<DailyNoteEntity[]>([]);
    const [endDate, setEndDate] = useState(new Date());
    const [isEndDateValid, setIsEndDateValid] = useState(true);
    const [startDate, setStartDate] = useState(() => new Date(new Date().setDate(new Date().getDate() - 6)));
    const [isStartDateValid, setIsStartDateValid] = useState(true);
    const [isEndBeforeStart, setIsEndBeforeStart] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [ascSort, setAscSort] = useState(true);
    const [showAllMethods, setShowAllMethods] = useState(false);
    const [employeesView, setEmployeesView] = useState(true);
    const role = checkTokenRole();
    const [openOwnerModal, setOpenOwnerModal] = useState(false);
    const [isOwnerModalOpenable, setIsOwnerModalOpenable] = useState(false);
    const theme = useTheme();
    const mobileBreakpoint = useMediaQuery(theme.breakpoints.down('sm'));
    const [chartData, setChartData] = useState<any[]>([]);
    const [datesFromTable, setDatesFromTable] = useState<Date[]>([]);
    const [openTicketModal, setOpenTicketModal] = useState(false);
    const [openSecretTicketModal, setOpenSecretTicketModal] = useState(false);
    const [ticketInput, setTicketInput] = useState("");
    const [ticketing, setTicketing] = useState(false);
    const [dailyTicketingItems, setDailyTicketingItems] = useState<DailyTicketEntity[]>([]);
    const [ticketItems, setTicketItems] = useState<TicketItemEntity[]>([]);
    const [dailyTicketingStatuses, setDailyTicketingStatuses] = useState<DailyTicketingStatusEntity[]>([]);
    const [controllerModificationDates, setControllerModificationDates] = useState<Set<string>>(new Set());
    const { t } = useTranslation();


    useEffect(() => {
        fetchData();
    }, [seasonId]);



    useEffect(() => {
        fetchSections();
    }, [departments]);

    useEffect(() => {
        fetchData();
        fetchControllerModifications();
    }, [endDate, startDate, ticketing]);

    useEffect(() => {
        fetchDepchiefs();
    }, [seasonId, startDate, endDate]);

    useEffect(() => {
        reverseData();
    }, [ascSort]);

    const fetchData = () => {
        fetchPaymentMethods();
        fetchDepartments();
        fetchSections();
        fetchDeposits();
        fetchDailyNotes();
        if (ticketing)
            fetchTickets();
    };

    const reverseData = () => {
        setSections([...sections].reverse());
        setDepartments([...departments].reverse());
        setPaymentMethods([...paymentMethods].reverse());
    };

    const fetchTickets = () => {
        if (ticketing && role !== 'DEPCHIEF') {
            ticketApi.findAllDaily(ignoreUTC(startDate).toISOString(), ignoreUTC(endDate).toISOString()).then((response) => { setDailyTicketingItems(response.data) });
            ticketApi.findAll().then((r) => {
                setTicketItems(r.data);
            });
            ticketApi.findStatuses(+startDate, +endDate).then((r) => {
                setDailyTicketingStatuses(r.data);
            })
        }
    }

    const fetchDepchiefs = async () => {
        if ((role === 'OWNER' || role === 'CONTROLLER')) {
            const depchiefs = (await employeesApi.findAllDepchief(seasonId, true)).data;
            let removedEmployees: EmployeeEntity[] = [];
            if (seasonId) {
                removedEmployees = (await employeesApi.findRemovedFromTurnovers(
                    seasonId,
                    startDate.toISOString().split("T")[0],
                    endDate.toISOString().split("T")[0])).data;
            }
            
            // Get IDs of employees already in depchiefs to avoid duplicates
            const depchiefIds = new Set(depchiefs.map(emp => emp.id));
            
            // Filter out removed employees that are already in depchiefs
            // This prevents terminated employees from appearing twice
            const uniqueRemovedEmployees = removedEmployees.filter(emp => !depchiefIds.has(emp.id));
            
            const data: ExtendEmployeeEntity[] = depchiefs.map(employee => {
                return {
                    isRemoved: employee.employeesToSeasons?.at(0)?.statusType === 'TERMINATED',
                    ...employee
                }
            }).concat(uniqueRemovedEmployees.map(employee => {
                return {
                    isRemoved: true,
                    ...employee
                }
            }));
            if (ascSort) {
                data.sort((a, b) => {
                    return a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase());
                });
            } else {
                data.sort((a, b) => {
                    return -a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase());
                });
            }
            setTurnoverEmployees(data);
        }
    };

    const fetchPaymentMethods = () => {
        paymentMethodsApi.findAll().then(({ data }) => {
            if (ascSort) {
                data.sort((a, b) => {
                    return translatePaymentTypes(a.type, t).toLowerCase().localeCompare(translatePaymentTypes(b.type, t).toLowerCase());
                });
                setPaymentMethods(data);
            } else {
                data.sort((a, b) => {
                    return -translatePaymentTypes(a.type, t).toLowerCase().localeCompare(translatePaymentTypes(b.type, t).toLowerCase());
                });
                setPaymentMethods(data);
            }
        });
    };

    const fetchDepartments = () => {
        if (role !== 'USER' && role !== 'MANAGER') {
            departmentsApi.findByRole(seasonId).then(({ data }) => {
                if (ascSort) {
                    data.sort((a, b) => {
                        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                    });
                } else {
                    data.sort((a, b) => {
                        return -a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                    });
                }
                setDepartments(data);
            });
        }
    };

    const fetchSections = () => {
        if (role !== 'USER' && role !== 'MANAGER') {
            paymentSectionsApi.findAll(seasonId).then(async ({ data }) => {
                if (ascSort) {
                    data.sort((a, b) => {
                        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                    });
                } else {
                    data.sort((a, b) => {
                        return -a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                    });
                }
                setSections(data);
            });
        }
    };


    const fetchDeposits = () => {
        depositsApi.findByDate(startDate.getTime(), endDate.getTime()).then(({ data }) => {
            setDeposits(data);
        });

    };

    const fetchDailyNotes = () => {
        if (role === 'OWNER' || role === 'CONTROLLER') {
            dailyNotesApi.findAll().then(({ data }) => {
                setDailyNotes(data);
            });
        }
    };

    const fetchControllerModifications = async () => {
        if (role === 'OWNER' || role === 'CONTROLLER') {
            const modificationDates = new Set<string>();
            const dates = getDates(startDate, endDate);
            
            for (const date of dates) {
                try {
                    const hasModifications = await turnoverApi.checkControllerModification(date.getTime());
                    if (hasModifications.data) {
                        modificationDates.add(date.toISOString().split('T')[0]);
                    }
                } catch (error) {
                    console.error('Error checking controller modifications for date:', date, error);
                }
            }
            
            setControllerModificationDates(modificationDates);
        }
    };

    const handleOpenAddTurnoverModal = () => {
        if (sections.length === 1) {
            setSelectedSection(sections[0]);
            setOpenAddTurnoverModal(true);
        } else if (departments.length === 1) {
            setSelectedDepartment(departments[0]);
            const filteredSectionsByDepartment = sections.filter((s) => s.departmentId === departments[0].id);
            if (filteredSectionsByDepartment.length === 1) {
                setSelectedSection(filteredSectionsByDepartment[0]);
                setOpenAddTurnoverModal(true);
            } else {
                setOpenSelectSectionModal(true);
                setSectionModalOpensTicketingModal(false);

            }
        } else {
            setOpenSelectSectionModal(true);
            setSectionModalOpensTicketingModal(false);

        }
    };

    const handleOpenAddTicketingModal = () => {
        if (sections.length === 1) {
            setSelectedSection(sections[0]);
            setOpenTicketModal(true);
        } else if (departments.length === 1) {
            setSelectedDepartment(departments[0]);
            const filteredSectionsByDepartment = sections.filter((s) => s.departmentId === departments[0].id);
            if (filteredSectionsByDepartment.length === 1) {
                setSelectedSection(filteredSectionsByDepartment[0]);
                setOpenTicketModal(true);
            } else {
                setOpenSelectSectionModal(true);
                setSectionModalOpensTicketingModal(true);
            }
        } else {
            setOpenSelectSectionModal(true);
            setSectionModalOpensTicketingModal(true);
        }
    };

    const chartOptions: ApexOptions = {
        chart: {
            type: 'bar',
            height: 260,
            stacked: true,
            toolbar: {
                show: false
            },
            zoom: {
                enabled: false
            }
        },
        responsive: [{
            breakpoint: 480,
            options: {
                legend: {
                    position: 'bottom',
                    offsetX: -10,
                    offsetY: 0
                }
            }
        }],
        plotOptions: {
            bar: {
                columnWidth: '80%',
                horizontal: false,
                borderRadius: 10,
                borderRadiusApplication: 'end',
                borderRadiusWhenStacked: 'last',
                dataLabels: {
                    total: {
                        enabled: true,
                        style: {
                            fontSize: '13px',
                            fontWeight: 900
                        },
                        formatter: function (value) {
                            const numericValue = Number(value);
                            if (numericValue >= 1000000) {
                                return (numericValue / 1000000).toFixed(1) + 'M';
                            } else if (numericValue >= 1000) {
                                return (numericValue / 1000).toFixed(1) + 'K';
                            } else {
                                return numericValue.toFixed(2) + " €";
                            }
                        }
                    }
                }
            }
        },
        xaxis: {
            categories: datesFromTable.length > 0 ? datesFromTable : []
        },
        yaxis: {
            labels: {
                formatter: function (value) {
                    if (value && value >= 1000000) {
                        return (value / 1000000).toFixed(1) + 'M';
                    } else if (value && value >= 1000) {
                        return (value / 1000).toFixed(1) + 'K';
                    } else {
                        return value ? value.toFixed(0) : '0';
                    }
                }
            }
        },
        dataLabels: {
            enabled: true,
            formatter: (value) => {
                const numericValue = Number(value);
                if (numericValue >= 1000000) {
                    return (numericValue / 1000000).toFixed(1) + 'M';
                } else if (numericValue >= 1000) {
                    return (numericValue / 1000).toFixed(1) + 'K';
                }
                return numericValue.toFixed(2) + " €";
            },
            style: {
                fontSize: '12px',
                fontWeight: 700
            },
            background: {
                enabled: false,
                borderRadius: 3,
                padding: 4
            }
        },
        legend: {
            position: 'right',
            offsetY: 40
        },
        fill: {
            opacity: 1
        }
    };

    const excludedKeys = new Set(['isDepartment', 'isSection', 'name', 'department', 'empty', 'id', 'sectionIds', 'total']);

    const chartSeries = chartData
        .filter(({ isDepartment, isSection, total }) => isDepartment && !isSection && total > 0)
        .map(({ name, ...rest }) => ({
            name: name.length > 10 ? name.substring(0, 10) + '...' : name,
            data: Object.entries(rest)
                .filter(([key, value]) => {
                    return !excludedKeys.has(key) && (typeof value === 'number' || value === null);
                })
                .map(([_, value]) => (typeof value === 'number' ? Number(value.toFixed(2)) : null)),
            type: 'bar'
        }));

    return (
        <>
            {(role === 'OWNER' || role === 'DEPCHIEF' || role === 'CONTROLLER') && (
                <>
                    <FlexBox marginTop={1} justifyContent={'end'} maxHeight={60}>
                        {(departments.length <= 0 || sections.length <= 0) && (
                            <Small color={theme.palette.warning.main} marginRight={2} width={300}>
                                {t('turnovers.cannotAdd')}
                            </Small>
                        )}
                        <Button
                            sx={{ marginRight: 1 }}
                            variant="contained"
                            disabled={
                                departments.length <= 0 ||
                                sections.length <= 0 ||
                                paymentMethods.length <= 0
                            }
                            startIcon={<Download />}
                            onClick={async () => {
                                const { data } = await turnoverApi.findAll(false);
                                turnoversReport(
                                    departments,
                                    sections,
                                    paymentMethods,
                                    data,
                                    deposits,
                                    startDate,
                                    endDate,
                                    seasonId
                                );
                            }}
                        >
                            {t('turnover.report')}
                        </Button>
                        {role === 'OWNER' && (
                            <Button
                                sx={{ marginRight: 1 }}
                                variant="contained"
                                disabled={departments.length <= 0 || sections.length <= 0}
                                startIcon={<Add />}
                                onClick={() => {
                                    setOpenAddDepositModal(true);
                                    setDepositDate(undefined);
                                }}
                            >
                                {t('deposit.add')}
                            </Button>
                        )}
                        {role === 'OWNER' && (
                            <Button
                                sx={{ marginRight: 1 }}
                                variant="contained"
                                disabled={!isOwnerModalOpenable}
                                startIcon={<Add />}
                                onClick={() => {
                                    setOpenOwnerModal(true);
                                }}
                            >
                                {t('ownerConfirmModal.openButton')}
                            </Button>
                        )}
                        { (
                            <Button
                                variant="contained"
                                disabled={departments.length <= 0 || sections.length <= 0}
                                startIcon={<Add />}
                                onClick={() => {
                                    handleOpenAddTurnoverModal();
                                }}
                            >
                                {t('amount.add')}
                            </Button>
                        )}
                        {ticketing && (
                            <Button
                                variant="contained"
                                disabled={departments.length <= 0 || sections.length <= 0}
                                startIcon={<Add />}
                                style={{ marginLeft: 10 }}
                                onClick={() => {
                                    handleOpenAddTicketingModal();
                                }}
                            >
                                {t('add.ticketing')}
                            </Button>
                        )}
                    </FlexBox>
                    <SelectSectionModal
                        open={openSelectSectionModal}
                        onClose={() => {
                            setOpenSelectSectionModal(false);
                            setSelectedDepartment(undefined);
                            setSelectedSection(undefined);
                            fetchData();
                        }}
                        onConfirm={() => {
                            sectionModalOpensTicketingModal && ticketing ? setOpenTicketModal(true) :
                                setOpenAddTurnoverModal(true);
                            setOpenSelectSectionModal(false);
                        }}
                        onSectionChange={(section) => { setSelectedSection(section) }}
                        sections={sections}
                        ticketing={sectionModalOpensTicketingModal}
                        selectedSection={selectedSection}
                        setSelectedDepartment={(department) => {
                            setSelectedDepartment(department);
                        }}
                        ticketingSections={sections}
                        departments={departments}
                        selectedDepartment={selectedDepartment}
                    />
                    <AddTurnoverModal
                        open={openAddTurnoverModal}
                        section={selectedSection}
                        isOwner={role === 'OWNER' || role === 'CONTROLLER'}
                        isController={role === 'CONTROLLER'}
                        onClose={() => {
                            setOpenAddTurnoverModal(false);
                            setSelectedDepartment(undefined);
                            setSelectedSection(undefined);
                            fetchData();
                        }}
                        onBack={() => {
                            setOpenAddTurnoverModal(false);
                            const filteredSectionsByDepartemnt = sections.filter(
                                (s) => s.departmentId === departments[0].id
                            );
                            if (
                                sections.length === 1 ||
                                (departments.length === 1 && filteredSectionsByDepartemnt.length === 1)
                            ) {
                                setSelectedDepartment(undefined);
                                setSelectedSection(undefined);
                                fetchData();
                            } else {
                                setOpenSelectSectionModal(true);
                            }
                        }}
                    />
                    {ticketing && (
                        <AddTicketModal
                            open={openTicketModal}
                            section={selectedSection}
                            isOwner={role === 'OWNER'}
                            onClose={() => {
                                setOpenTicketModal(false);
                                setSelectedDepartment(undefined);
                                setSelectedSection(undefined);
                                setTicketing(false);
                            }}
                            fetchDataCallBack={fetchData}
                        />)}
                    {role === 'OWNER' && (
                        <OwnerConfirmModal
                            open={openOwnerModal}
                            fetchTableData={fetchData}
                            onClose={() => {
                                setOpenOwnerModal(false);
                            }}
                            setOpenable={setIsOwnerModalOpenable}
                        />
                    )}
                    {role === 'OWNER' && (
                        <SimplifiedAddDepositModal
                            open={openAddDepositModal}
                            onClose={() => {
                                setOpenAddDepositModal(false);
                            }}
                            defaultDate={depositsDate}
                            fetchTableData={fetchData}
                        />
                    )}
                </>
            )}
            {role === 'DEPCHIEF' && (
                <Container
                    maxWidth="lg"
                    sx={{
                        height: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        position: 'relative',
                        paddingLeft: mobileBreakpoint ? '20px' : '40px',
                        paddingRight: mobileBreakpoint ? '20px' : '40px'
                    }}
                >
                    {mobileBreakpoint && (
                        <img
                            src={Logo}
                            alt="OneStaff Logo"
                            style={{
                                width: '80px',
                                height: 'auto',
                                position: 'absolute',
                                top: 20,
                                left: '50%',
                                transform: 'translateX(-50%)'
                            }}
                        />
                    )}
                    <Grid
                        container
                        sx={{
                            flexGrow: 1,
                            justifyContent: mobileBreakpoint ? 'center' : 'flex-start',
                            alignItems: 'center',
                            textAlign: 'left'
                        }}
                    >
                        <Grid item xs={12} md={6} sx={{ order: mobileBreakpoint ? 2 : 1 }}>
                            <img
                                src={DefaultUserImage}
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: mobileBreakpoint ? '200px' : '400px'
                                }}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            md={6}
                            sx={{
                                order: mobileBreakpoint ? 1 : 2,
                                marginTop: mobileBreakpoint ? '20px' : '0'
                            }}
                        >
                            <div
                                style={{
                                    fontSize: mobileBreakpoint ? '36px' : '64px',
                                    lineHeight: '1.2',
                                    textAlign: 'left',
                                    marginLeft: '30px'
                                }}
                            >
                                <span style={{ color: 'black' }}>{t('greetings')}</span>
                                <br />
                                <span style={{ color: 'violet', fontWeight: 'bold' }}>
                                    {user?.username || t('user.guest')}
                                </span>
                            </div>
                        </Grid>
                    </Grid>
                    {!mobileBreakpoint && (
                        <img
                            src={Logo}
                            alt="OneStaff Logo"
                            onDoubleClick={() => {
                                setOpenSecretTicketModal(true);
                            }}
                            style={{
                                width: '120px',
                                height: 'auto',
                                position: 'absolute',
                                bottom: 20,
                                left: '50%',
                                transform: 'translateX(-50%)'
                            }}
                        />
                    )}
                </Container>
            )}
            {(role === 'OWNER' || role === 'CONTROLLER') && (
                <Grid container>
                    <Grid item xs={12} md={2} marginTop={2} marginBottom={2} marginRight={1}>
                        <SearchInput
                            bordered={false}
                            placeholder={t('common.tables.button.searchItems', {
                                items: t('menu.apps.sections')
                            })}
                            onChange={(e) => {
                                setSearchValue(e.target.value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={5} md={2} marginTop={1} marginBottom={2} marginRight={1}>
                        <CalendarInput
                            format="dd.MM.yyyy"
                            value={startDate}
                            onChange={(newValue) => {
                                if (newValue && newValue instanceof Date && !isNaN(+newValue)) {
                                    setStartDate(newValue);
                                    setIsStartDateValid(true);
                                    if (+startOfDay(newValue) > +endOfDay(endDate)) {
                                        setIsEndBeforeStart(true);
                                    } else {
                                        setIsEndBeforeStart(false);
                                    }
                                } else {
                                    setIsStartDateValid(false);
                                }
                            }}
                            slotProps={{
                                textField: {
                                    helperText: (!isStartDateValid && t('date.error')),
                                    error: !isStartDateValid || isEndBeforeStart
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={5} md={2} marginTop={1} marginBottom={2} marginRight={1}>
                        <CalendarInput
                            format="dd.MM.yyyy"
                            value={endDate}
                            onChange={(newValue) => {
                                if (newValue && newValue instanceof Date && !isNaN(+newValue)) {
                                    setEndDate(newValue);
                                    setIsEndDateValid(true);
                                    if (+endOfDay(newValue) < +startOfDay(startDate)) {
                                        setIsEndBeforeStart(true);
                                    } else {
                                        setIsEndBeforeStart(false);
                                    }
                                } else {
                                    setIsEndDateValid(false);
                                }
                            }}
                            slotProps={{
                                textField: {
                                    helperText:
                                        (!isEndDateValid && t('date.error')) ||
                                        (isEndBeforeStart && t('date.endBeforeStart')),
                                    error: !isEndDateValid || isEndBeforeStart
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={8} justifyContent={"start"}>
                        <Button
                            style={{ marginBottom: 20, marginRight: 50 }}
                            startIcon={ascSort ? <ArrowDownward /> : <ArrowUpward />}
                            onClick={() => {
                                setAscSort(!ascSort);
                            }}
                        >
                            {ascSort ? "ABC" : "CBA"}
                        </Button>
                        <Button
                            style={{ marginBottom: 20, marginRight: 50 }}
                            startIcon={showAllMethods ? <CheckBox /> : <CheckBoxOutlineBlank />}
                            onClick={() => {
                                setShowAllMethods(!showAllMethods);
                            }}
                        >
                            {t('turnover.show')}
                        </Button>
                        <Button
                            style={{ marginBottom: 20, marginRight: 50 }}
                            startIcon={!employeesView ? <CheckBox /> : <CheckBoxOutlineBlank />}
                            onClick={() => {
                                setEmployeesView(!employeesView);
                            }}
                        >
                            {t('turnover.employeesView')}
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={4} justifyContent={"end"}>
                        <Button
                            fullWidth
                            style={{ marginBottom: 20 }}
                            startIcon={<Info />}
                            onDoubleClick={() => {
                                setOpenSecretTicketModal(true);
                            }}
                            sx={{
                                textAlign: 'start'
                            }}
                        >
                            {t('turnover.infoEdit')}
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <TurnoverDataTable
                            startDate={startDate}
                            endDate={endDate}
                            departments={departments}
                            sections={sections}
                            paymentMethods={paymentMethods}
                            deposits={deposits}
                            employees={turnoverEmployees}
                            dailyNotes={dailyNotes}
                            fetchData={fetchData}
                            searchValue={searchValue}
                            showEmpty={showAllMethods}
                            employeesView={employeesView}
                            ticketing={ticketing}
                            openDepositModal={(date: Date) => {
                                setDepositDate(date);
                                setOpenAddDepositModal(true);
                            }}
                            setChartDataCallback={setChartData}
                            chartOptions={chartOptions}
                            chartSeries={chartSeries}
                            setDateFromTableCallback={setDatesFromTable}
                            dailyTicketingItems={dailyTicketingItems}
                            ticketItems={ticketItems}
                            ticketingStatuses={dailyTicketingStatuses}
                            controllerModificationDates={controllerModificationDates}
                        />
                    </Grid>
                </Grid>
            )}
            <Modal open={openSecretTicketModal} onClose={() => { setOpenSecretTicketModal(false); setTicketInput(""); }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <H2>
                        {t('ticekts.inputPassword')}
                    </H2>
                    <FlexBox alignItems={'center'} mt={2}>
                        <TextField
                            sx={{
                                display: 'flex'
                            }}
                            value={ticketInput}
                            onChange={(e) => {
                                setTicketInput(e.target.value);
                            }}

                        />

                        <IconButton
                            onClick={() => {
                                if (ticketInput === "tickets") {
                                    setTicketing(true);
                                }
                                setTicketInput("");
                                setOpenSecretTicketModal(false);
                            }}
                            sx={{
                                ml: 1,
                                display: 'flex'
                            }}
                        >
                            <Send />
                        </IconButton>
                    </FlexBox>
                </Box>
            </Modal>
        </>
    );
};

export default TurnoverSummary;
